import React, { useState, useEffect } from 'react';
import { Cookie, Shield, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';

const COOKIE_CONSENT_KEY = 'willkart_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'willkart_cookie_preferences';

// Cookie categories
const COOKIE_CATEGORIES = {
  essential: {
    name: 'Essential Cookies',
    description: 'These cookies are necessary for the website to function properly. They enable core functionality such as security, authentication, and session management.',
    required: true,
    cookies: ['Session tokens', 'Authentication data', 'Security tokens']
  },
  functional: {
    name: 'Functional Cookies',
    description: 'These cookies enable personalized features such as remembering your login email and preferences.',
    required: false,
    cookies: ['Remembered email addresses', 'User preferences', 'UI settings']
  },
  analytics: {
    name: 'Analytics Cookies',
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    required: false,
    cookies: ['Page views', 'User behavior', 'Performance metrics']
  }
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be changed
    functional: true,
    analytics: true
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true
    };
    saveConsent(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false
    };
    saveConsent(essentialOnly);
    // Clear non-essential stored data
    clearNonEssentialCookies();
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    if (!preferences.functional) {
      clearFunctionalCookies();
    }
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
  };

  const clearNonEssentialCookies = () => {
    // Clear functional cookies (remembered emails, preferences)
    clearFunctionalCookies();
    // Note: Analytics cookies would be cleared here if we had any
  };

  const clearFunctionalCookies = () => {
    // Clear remembered email addresses
    localStorage.removeItem('willkart_partner_remembered_email');
    localStorage.removeItem('willkart_client_remembered_email');
    // Clear any other functional preferences
    localStorage.removeItem('willkart_ui_preferences');
  };

  const handlePreferenceChange = (category) => {
    if (category === 'essential') return; // Can't change essential
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isVisible) return null;

  // Suppress the end-user cookie banner on admin/partner backoffice routes —
  // admins shouldn't be prompted with the same prompt as end users, and the
  // overlay intercepts pointer events on the login button. Placed AFTER all
  // hooks to comply with the rules of hooks (consistent hook order).
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  if (/^\/(admin|partner|trusted)(\/|$)/.test(pathname)) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Cookie Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-slate-900 border-t border-slate-700 shadow-2xl"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
          {/* Main Content */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
            {/* Icon and Text */}
            <div className="flex-1">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg shrink-0">
                  <Cookie className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    We Value Your Privacy
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, remember your preferences, and analyze site traffic. 
                    You can choose to accept all cookies or customize your preferences.
                  </p>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="mt-2 text-amber-400 hover:text-amber-300 text-sm font-medium flex items-center gap-1 transition-colors"
                    data-testid="cookie-details-toggle"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Manage Preferences
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
              <Button
                onClick={handleRejectNonEssential}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-6"
                data-testid="cookie-reject-btn"
              >
                Essential Only
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-6"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
            </div>
          </div>

          {/* Detailed Preferences */}
          {showDetails && (
            <div className="mt-6 border-t border-slate-700 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => (
                  <div 
                    key={key}
                    className={`p-4 rounded-lg border ${
                      preferences[key] 
                        ? 'bg-slate-800/50 border-amber-500/30' 
                        : 'bg-slate-800/30 border-slate-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <h4 className="font-medium text-white text-sm">{category.name}</h4>
                        {category.required && (
                          <span className="text-xs text-amber-400">Always Active</span>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[key]}
                          onChange={() => handlePreferenceChange(key)}
                          disabled={category.required}
                          className="sr-only peer"
                          data-testid={`cookie-toggle-${key}`}
                        />
                        <div className={`w-9 h-5 rounded-full peer transition-colors ${
                          category.required 
                            ? 'bg-amber-500 cursor-not-allowed' 
                            : preferences[key] 
                              ? 'bg-amber-500' 
                              : 'bg-slate-600'
                        } peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500/50 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all ${
                          preferences[key] ? 'after:translate-x-4' : ''
                        }`} />
                      </label>
                    </div>
                    <p className="text-slate-400 text-xs leading-relaxed mb-2">
                      {category.description}
                    </p>
                    <div className="text-xs text-slate-500">
                      <span className="font-medium">Includes:</span> {category.cookies.join(', ')}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Shield className="w-4 h-4" />
                  <span>
                    Read our{' '}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:text-amber-300 underline"
                    >
                      Privacy Policy
                    </a>
                    {' '}for more information.
                  </span>
                </div>
                <Button
                  onClick={handleSavePreferences}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6"
                  data-testid="cookie-save-preferences-btn"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Utility function to check if functional cookies are allowed
export const areFunctionalCookiesAllowed = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!consent) return false; // No consent given yet
  
  const preferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!preferences) return true; // Consent given but no preferences = accept all
  
  try {
    const parsed = JSON.parse(preferences);
    return parsed.functional === true;
  } catch {
    return true;
  }
};

// Utility function to check if analytics cookies are allowed
export const areAnalyticsCookiesAllowed = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!consent) return false;
  
  const preferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!preferences) return true;
  
  try {
    const parsed = JSON.parse(preferences);
    return parsed.analytics === true;
  } catch {
    return true;
  }
};

// Utility function to safely store functional data (respects cookie preferences)
export const setFunctionalStorage = (key, value) => {
  if (areFunctionalCookiesAllowed()) {
    localStorage.setItem(key, value);
    return true;
  }
  return false;
};

// Utility function to reset cookie consent (for settings pages)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_PREFERENCES_KEY);
  window.location.reload();
};

export default CookieConsent;
